import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star, Clock, MapPin, ChevronRight, Flame, Percent, Gift, Truck, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MobileLayout } from "@/components/layouts/MobileLayout";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import API_URL from "@/utils/api";
import Logo from "@/components/ui/Logo";

const API = API_URL;

const RESTAURANT_LOGO = "https://customer-assets.emergentagent.com/job_taco-tracker-4/artifacts/49jtitvp_2NARANJAlogo%20ordenado%20definitivo.png";

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [config, setConfig] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, menuRes, configRes, offersRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/menu`),
          axios.get(`${API}/config`),
          axios.get(`${API}/offers`),
        ]);
        setCategories(catRes.data);
        setBestSellers(menuRes.data.filter((item) => item.best_seller).slice(0, 4));
        const configData = configRes.data;
        setConfig(configData);
        setOffers(offersRes.data);
        const primaryColor = configData?.primary_color || configData?.branding?.primary_color;

        // Inject Branding Colors
        if (primaryColor) {
          document.documentElement.style.setProperty('--primary', primaryColor);
          document.documentElement.style.setProperty('--accent', primaryColor);
          
          // Derive RGB for transparency effects
          if (primaryColor.startsWith('#')) {
            const r = parseInt(primaryColor.slice(1, 3), 16);
            const g = parseInt(primaryColor.slice(3, 5), 16);
            const b = parseInt(primaryColor.slice(5, 7), 16);
            document.documentElement.style.setProperty('--primary-rgb', `${r}, ${g}, ${b}`);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const getDiscountIcon = (type) => {
    switch (type) {
      case "percentage": return Percent;
      case "2x1": return Gift;
      case "fixed_price": return Tag;
      case "free_delivery": return Truck;
      default: return Gift;
    }
  };

  const getDiscountLabel = (offer) => {
    switch (offer.discount_type) {
      case "percentage": return `${offer.discount_value}% OFF`;
      case "2x1": return "2x1";
      case "fixed_price": return `${offer.discount_value.toFixed(2)}€`;
      case "free_delivery": return "Envío Gratis";
      default: return "";
    }
  };

  const getDiscountColor = (type) => {
    switch (type) {
      case "percentage": return "from-red-500 to-orange-500";
      case "2x1": return "from-purple-500 to-pink-500";
      case "fixed_price": return "from-green-500 to-teal-500";
      case "free_delivery": return "from-blue-500 to-cyan-500";
      default: return "from-yellow-500 to-orange-500";
    }
  };

  return (
    <MobileLayout>
      {/* Hero Section */}
      <section className="relative h-[65vh] min-h-[450px] overflow-hidden bg-black">
        {/* Mosaico sutil de fondo */}
        <div
          className="absolute inset-0 bg-repeat opacity-[0.1]"
          style={{
            backgroundImage: config?.logo ? `url(${config.logo})` : 'none',
            backgroundSize: '100px',
            filter: 'grayscale(1) brightness(0.5)'
          }}
        />
        {/* Gradiente oscuro sobre el mosaico */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />

        <div className="relative h-full flex flex-col justify-end p-6 pb-12">
          <div className="space-y-4 animate-slide-up">
            {/* Logo & Branding */}
            <div className="flex flex-col gap-4 mb-2">
              {config?.logo ? (
                <img 
                  src={config.logo} 
                  alt={config.name} 
                  className="h-16 w-auto object-contain self-start drop-shadow-2xl" 
                />
              ) : (
                <Logo size={32} name={config?.restaurant_name || config?.name} className="!gap-4" />
              )}
              <div>
                <p className="text-gray-300 flex items-center gap-1 font-bold">
                  <Flame className="w-4 h-4 text-primary" style={{ color: 'var(--primary)' }} />
                  {config?.name || "El sistema operativo de tu negocio"}
                </p>
              </div>
            </div>

            <p className="text-gray-300 text-base max-w-md">
              Vive la mejor experiencia culinaria con nosotros. Calidad, sabor y servicio excepcional en cada bocado.
            </p>

            <div className="flex items-center gap-4 text-gray-400 text-sm">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-primary text-primary" style={{ fill: 'var(--primary)', color: 'var(--primary)' }} />
                <span className="text-white">4.9</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-primary" style={{ color: 'var(--primary)' }} />
                <span>20-35 min</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 text-primary" style={{ color: 'var(--primary)' }} />
                <span>Almería</span>
              </div>
            </div>

            <Button
              size="lg"
              className="bg-primary hover:opacity-90 text-black rounded-full px-8 font-bold shadow-lg shadow-primary/30 active:scale-95 transition-transform"
              onClick={() => navigate("/menu")}
              data-testid="hero-order-btn"
              style={{ backgroundColor: 'var(--primary)' }}
            >
              <Flame className="w-5 h-5 mr-2" />
              VER MENÚ
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Offers Section */}
      {offers.length > 0 && (
        <section className="px-4 py-6 bg-gray-950">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
              <Gift className="w-5 h-5 text-pink-500" />
              OFERTAS DEL DÍA
            </h2>
          </div>

          <div className="space-y-3">
            {offers.map((offer, index) => {
              const DiscountIcon = getDiscountIcon(offer.discount_type);
              return (
                <div
                  key={offer.offer_id}
                  onClick={() => navigate("/menu")}
                  className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer bg-gradient-to-r ${getDiscountColor(offer.discount_type)} animate-fade-in hover:scale-[1.02] transition-transform`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`offer-${offer.offer_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                      <DiscountIcon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-lg text-white">
                          {offer.name}
                        </h3>
                        <Badge className="bg-white/20 text-white border-0">
                          {getDiscountLabel(offer)}
                        </Badge>
                      </div>
                      <p className="text-white/80 text-sm">
                        {offer.description}
                      </p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-white/60" />
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full" />
                  <div className="absolute -right-2 -bottom-6 w-16 h-16 bg-white/10 rounded-full" />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="px-4 py-8 bg-gray-950">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-primary" style={{ color: 'var(--primary)' }}>CATEGORÍAS</h2>
          <Link
            to="/menu"
            className="text-primary text-sm font-medium flex items-center gap-1"
            style={{ color: 'var(--primary)' }}
          >
            Ver todo <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.slice(0, 4).map((category, index) => (
            <Link
              key={category.category_id}
              to={`/menu?category=${category.category_id}`}
              data-testid={`category-${category.name.toLowerCase()}`}
              className="group relative aspect-square rounded-xl overflow-hidden animate-fade-in border border-gray-800 hover:border-primary transition-colors"
              style={{ animationDelay: `${index * 100}ms`, '--hover-border-color': 'var(--primary)' }}
            >
              <img
                src={category.image}
                alt={category.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="font-heading font-bold text-white text-base">
                  {category.name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Best Sellers Section */}
      <section className="px-4 py-8 bg-black">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
              <Flame className="w-5 h-5 text-orange-500" />
              LOS MÁS PEDIDOS
            </h2>
            <p className="text-gray-400 text-sm">
              Nuestros favoritos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {bestSellers.map((item, index) => (
            <Link
              key={item.item_id}
              to={`/menu/${item.item_id}`}
              data-testid={`bestseller-${item.item_id}`}
              className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-primary transition-colors animate-slide-up"
              style={{ animationDelay: `${index * 100}ms`, borderColor: 'var(--primary-hover, #1f2937)' }}
            >
              <div className="flex">
                <div className="w-28 h-28 flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 p-4 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-heading font-bold text-white text-base line-clamp-1">
                        {item.name}
                      </h3>
                      <span className="bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-bold text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        TOP
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2 mt-1">
                      {item.description}
                    </p>
                  </div>
                  <p className="text-primary font-bold text-lg" style={{ color: 'var(--primary)' }}>{item.price.toFixed(2)} €</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Promo Section */}
      {config?.features?.delivery === true && (
        <section className="px-4 py-8 bg-gray-950">
          <div className="bg-gradient-to-r from-primary/80 to-primary rounded-xl p-6 text-black" style={{ background: 'linear-gradient(to right, rgba(var(--primary-rgb), 0.8), var(--primary))' }}>
            <h3 className="font-heading font-bold text-xl mb-2">
              🔥 PRIMER PEDIDO: ENVÍO GRATIS
            </h3>
            <p className="text-black/80 text-sm mb-4">
              Regístrate ahora y disfruta de envío gratis en tu primera orden.
            </p>
            {!user && (
              <Button
                variant="secondary"
                className="bg-black text-yellow-500 hover:bg-gray-900 rounded-full font-bold"
                onClick={() => navigate("/login")}
                data-testid="home-register-btn"
              >
                Registrarse gratis
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Restaurant Info */}
      <section className="px-4 py-8 bg-black">
        <h2 className="font-heading text-xl font-bold text-primary mb-4" style={{ color: 'var(--primary)' }}>INFORMACIÓN</h2>
        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-3 text-gray-300">
            <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
            <div>
              <p className="font-medium text-white">Dirección</p>
              <p>Almería, España</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-gray-300">
            <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
            <div>
              <p className="font-medium text-white">Horario</p>
              <p>Lun - Dom: 12:00 - 23:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Staff Access */}
      <footer className="px-4 py-6 bg-gray-950 border-t border-gray-800">
        <div className="text-center space-y-3 mb-4">
          <p className="text-gray-400 text-xs font-medium">
            Powered by <span className="text-primary font-bold" style={{ color: 'var(--primary)' }}>Conexo POS</span>
          </p>
          <p className="text-gray-600 text-[10px]">
            © 2026 Conexo OS — Premium SaaS Solution
          </p>
        </div>
        <div className="flex items-center justify-center pt-3 border-t border-gray-800">
          <Link
            to="/staff"
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors font-medium"
            data-testid="staff-access-btn"
          >
            Acceso Personal
          </Link>
        </div>
      </footer>
    </MobileLayout>
  );
}
